import { useEffect, useRef, useState } from "react";

import { digitalHumanService, type DhTaskDto } from "../api/digitalHumanService";
import { errorText } from "@/shared/lib/apiErrors";

/** 未终态(仍在出片)→ 继续轮询;终态停。 */
const ACTIVE_STATUSES = new Set<DhTaskDto["status"]>(["PENDING", "RUNNING"]);

/** 轮询数字人任务详情(镜像 useVideoTaskPolling):ACTIVE 态每 4s 拉一次,终态停;返 {task,error,refresh}。 */
export function useDhTaskPolling(taskId: number | null) {
  const [task, setTask] = useState<DhTaskDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // taskId 变化时清空旧态(避免串号闪显)。
  useEffect(() => {
    setTask(null);
    setError(null);
  }, [taskId]);

  useEffect(() => {
    if (taskId == null) return;
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const t = await digitalHumanService.getTask(taskId);
        if (!cancelled) setTask(t);
        return t;
      } catch (e) {
        if (!cancelled) setError(errorText(e));
        return null;
      }
    };

    const loop = async () => {
      const t = await fetchOnce();
      if (cancelled) return;
      if (!t || !ACTIVE_STATUSES.has(t.status)) return; // 终态 / 失败 → 停
      timerRef.current = setTimeout(loop, 4000);
    };

    loop();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [taskId, refreshTick]);

  return { task, error, refresh: () => setRefreshTick((n) => n + 1) };
}
